import React from "react";
import {
  Box,
  Text,
  Progress,
  createStyles,
  useMantineTheme,
  Loader,
  Center,
} from "@mantine/core";
import { useGetDashboardStatistics } from "api/attendance/dashboardStatistics";
import type { DashboardStatistic } from "api/attendance/dashboardStatistics";
import { useGetUser } from "api/users/getUser";
import type { UseQueryResult } from "@tanstack/react-query";
import { useGetTotalHour } from "api/attendance/getUserWorkingHours";
// testing
const useStatsStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.sm,
    padding: theme.spacing.lg,
    // paddingBottom: theme.spacing.md,
    width: "100%",
  },
  header: {
    marginBottom: theme.spacing.md,
  },
  row: {
    marginBottom: theme.spacing.md,
    "&:last-of-type": { marginBottom: 0 },
  },
  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
}));

interface StatisticsProps {
  userId?: string | null;
}

export function Statistics({ userId }: StatisticsProps) {
  const { data: payload, isLoading } =
    useGetDashboardStatistics(userId || undefined) as UseQueryResult<DashboardStatistic, unknown>;

  const {
    data: workingHours,
    isLoading: hoursLoading,
    isError,
  } = useGetTotalHour(userId || undefined);

  const { classes } = useStatsStyles();
  const theme = useMantineTheme();

  if (isLoading || hoursLoading) {
    return (
      <Center style={{ height: 200 }}>
        <Loader />
      </Center>
    );
  }

  if (!payload) {
    return (
      <Box className={classes.card}>
        <Text color="red">No statistics available.</Text>
      </Box>
    );
  }

  const expectedDaily = workingHours?.totalHours ?? 0;

  const stats = [
    {
      label: "Today",
      value: payload.today.totalWorkingHours,
      max: expectedDaily,
      unit: "h",
    },
    {
      label: "This Week",
      value: payload.thisWeek.totalWorkingHours,
      max: expectedDaily * 5,
      unit: "h",
    },
    {
      label: "This Month",
      value: payload.thisMonth.totalWorkingHours,
      max: expectedDaily * 22,
      unit: "h",
    },
    {
      label: "Overtime",
      value: payload.overtime.hours,
      max: payload.overtime.maxHours,
      unit: "h",
    },
    {
      label: "Remaining",
      value: payload.remaining.hours,
      max: expectedDaily,
      unit: "h",
    },
  ];

  return (
    <Box className={classes.card}>
      <Text weight={500} size="lg" className={classes.header}>
        Statistics
      </Text>

      {stats.map(({ label, value, max, unit }) => {
        const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
        const displayValue = `${value.toFixed(2)}${unit} / ${max.toFixed(
          2
        )}${unit}`;
        const isEven = Math.floor(value) % 2 === 0;
        const barColor = isEven ? theme.colors.blue[6] : theme.colors.yellow[4];

        return (
          <Box key={label} className={classes.row}>
            <Box className={classes.labelRow}>
              <Text weight={500}>{label}</Text>
              <Text weight={500}>{displayValue}</Text>
            </Box>
            <Progress
              value={pct}
              size={8}
              radius="xl"
              styles={{
                bar: { backgroundColor: barColor },
                root: { backgroundColor: theme.colors.gray[2] },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}
